import { useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Pencil, Copy, Trash2 } from "lucide-react";
import { api, type Page } from "../../lib/api";
import { navigate } from "../../lib/route";

interface CmsSidebarProps {
  route: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

function statusDot(page: Page): { char: string; color: string; label: string } {
  if (page.IsPublished && !page.HasDraft) return { char: "●", color: "#10b981", label: "Live" };
  if (page.IsPublished && page.HasDraft) return { char: "◐", color: "#f59e0b", label: "Changes pending" };
  return { char: "○", color: "#94a3b8", label: "Draft" };
}

function activePageId(route: string): string | null {
  const match = route.match(/\/app\/pages\/([^/?]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

// Tiny icon button used for hover actions
function IconBtn({
  icon, title, onClick, danger,
}: {
  icon: React.ReactNode; title: string; onClick: (e: React.MouseEvent) => void; danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "transparent", border: "none",
        color: danger ? "#ef4444" : "var(--muted)",
        cursor: "pointer", padding: "3px", borderRadius: 4,
        opacity: 0.7, lineHeight: 1,
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = "0.7"; }}
    >
      {icon}
    </button>
  );
}

// ---- Page rows ---------------------------------------------------------------

interface RowProps {
  page: Page;
  isActive: boolean;
  collapsed: boolean;
  onClone: (id: string) => void;
  onDelete: (id: string) => void;
}

function HomePageRow({ page, isActive, collapsed, onClone }: RowProps) {
  const [hovered, setHovered] = useState(false);
  const dot = statusDot(page);

  if (collapsed) {
    return (
      <a
        href={`/app/pages/${page.ID}`}
        onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }}
        title={`${page.Title} — ${dot.label}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          height: 36, textDecoration: "none",
          background: isActive ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
          borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
        }}
      >
        <span style={{ color: dot.color, fontSize: "0.75rem", lineHeight: 1 }}>{dot.char}</span>
      </a>
    );
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex", alignItems: "center",
        borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
        background: isActive ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
      }}
    >
      <span style={{ width: 22, flexShrink: 0 }} />
      <a
        href={`/app/pages/${page.ID}`}
        onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }}
        title="/ — Home"
        style={{
          display: "flex", alignItems: "center", gap: "0.5rem",
          padding: "0.45rem 0.4rem 0.45rem 0", flex: 1,
          textDecoration: "none",
          color: isActive ? "var(--accent)" : "var(--text)",
          fontSize: "0.875rem", minWidth: 0,
        }}
      >
        <span style={{ color: dot.color, fontSize: "0.75rem", lineHeight: 1, flexShrink: 0 }}>{dot.char}</span>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {page.Title}
        </span>
      </a>
      {hovered && (
        <div style={{ display: "flex", gap: 1, marginRight: "0.35rem", flexShrink: 0 }}>
          <IconBtn icon={<Pencil size={12} />} title="Edit" onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }} />
          <IconBtn icon={<Copy size={12} />} title="Duplicate" onClick={e => { e.preventDefault(); onClone(page.ID); }} />
        </div>
      )}
    </div>
  );
}

function SortablePageRow({ page, isActive, collapsed, onClone, onDelete }: RowProps) {
  const [hovered, setHovered] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.ID });
  const dot = statusDot(page);

  if (collapsed) {
    return (
      <a
        ref={setNodeRef}
        href={`/app/pages/${page.ID}`}
        onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }}
        title={`${page.Title} — ${dot.label}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          height: 36, textDecoration: "none",
          transform: CSS.Transform.toString(transform), transition,
          opacity: isDragging ? 0.45 : 1,
          background: isActive ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
          borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
        }}
      >
        <span style={{ color: dot.color, fontSize: "0.75rem", lineHeight: 1 }}>{dot.char}</span>
      </a>
    );
  }

  return (
    <div
      ref={setNodeRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.45 : 1,
        display: "flex", alignItems: "center",
        borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
        background: isActive ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
      }}
    >
      <span
        {...attributes} {...listeners}
        style={{
          padding: "0.5rem 0.2rem 0.5rem 0.55rem",
          color: "var(--muted)", cursor: isDragging ? "grabbing" : "grab",
          fontSize: "0.75rem", userSelect: "none", touchAction: "none",
          opacity: 0.35, flexShrink: 0, lineHeight: 1,
        }}
        title="Drag to reorder"
      >⠿</span>
      <a
        href={`/app/pages/${page.ID}`}
        onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }}
        title={`/${page.Slug} — ${dot.label}`}
        style={{
          display: "flex", alignItems: "center", gap: "0.5rem",
          padding: "0.45rem 0.4rem 0.45rem 0.2rem", flex: 1,
          textDecoration: "none",
          color: isActive ? "var(--accent)" : "var(--text)",
          fontSize: "0.875rem", minWidth: 0,
        }}
      >
        <span style={{ color: dot.color, fontSize: "0.75rem", lineHeight: 1, flexShrink: 0 }}>{dot.char}</span>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {page.Title}
        </span>
      </a>
      {hovered && (
        <div style={{ display: "flex", gap: 1, marginRight: "0.35rem", flexShrink: 0 }}>
          <IconBtn icon={<Pencil size={12} />} title="Edit" onClick={e => { e.preventDefault(); navigate(`/app/pages/${page.ID}`); }} />
          <IconBtn icon={<Copy size={12} />} title="Duplicate" onClick={e => { e.preventDefault(); onClone(page.ID); }} />
          <IconBtn icon={<Trash2 size={12} />} title="Delete" danger onClick={e => { e.preventDefault(); onDelete(page.ID); }} />
        </div>
      )}
    </div>
  );
}

// ---- Main sidebar ------------------------------------------------------------

export function CmsSidebar({ route, collapsed, onToggleCollapsed }: CmsSidebarProps) {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const currentPageId = activePageId(route);
  const isPortfolioActive = route.startsWith("/app/portfolio");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const DEFAULT_SLUGS = ["home", "about", "galleries", "contact"];
      try {
        let list = await api.listPages();
        const hasSlugs = new Set(list.map(p => p.Slug));
        if (DEFAULT_SLUGS.some(s => !hasSlugs.has(s))) {
          await api.seedDefaultPages().catch(() => {});
          list = await api.listPages();
        }
        if (!cancelled) { setPages(list); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => { cancelled = true; };
  }, [route]);

  const homePage = pages.find(p => p.Slug === "home");
  const otherPages = pages.filter(p => p.Slug !== "home");

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = otherPages.findIndex(p => p.ID === active.id);
    const newIdx = otherPages.findIndex(p => p.ID === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = [...otherPages];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    setPages(homePage ? [homePage, ...reordered] : reordered);
    Promise.all(reordered.map((p, i) => api.updatePage(p.ID, { sort_order: i + 1 }))).catch(() => {
      void api.listPages().then(setPages);
    });
  }

  async function handleClone(id: string) {
    try {
      const clone = await api.clonePage(id);
      const list = await api.listPages();
      setPages(list);
      navigate(`/app/pages/${clone.ID}`);
    } catch {
      // silent
    }
  }

  async function handleDelete(id: string) {
    const page = pages.find(p => p.ID === id);
    if (!page || !window.confirm(`Delete "${page.Title}"? This cannot be undone.`)) return;
    try {
      await api.deletePage(id);
      const list = await api.listPages();
      setPages(list);
      // If we just deleted the active page, go back to pages list.
      if (id === currentPageId) navigate("/app/pages");
    } catch {
      // silent
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const page = await api.createPage({
        slug: newTitle.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
        title: newTitle.trim(),
      });
      setNewTitle("");
      setShowCreate(false);
      navigate(`/app/pages/${page.ID}`);
      const list = await api.listPages();
      setPages(list);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create page");
    } finally {
      setCreating(false);
    }
  }

  function handleNewPageClick() {
    if (collapsed) {
      // Expand first, then show create form on next tick.
      onToggleCollapsed();
      setTimeout(() => setShowCreate(true), 220);
    } else {
      setShowCreate(true);
    }
  }

  return (
    <aside style={{
      width: collapsed ? 48 : 240,
      flexShrink: 0,
      position: "sticky",
      top: 0,
      height: "100vh",
      overflow: "hidden",
      background: "var(--surface)",
      borderRight: "1px solid var(--border)",
      display: "flex",
      flexDirection: "column",
      transition: "width 0.2s",
    }}>
      {/* Header */}
      <div style={{
        padding: collapsed ? "0.75rem 0 0.5rem" : "0.75rem 1rem 0.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        flexShrink: 0,
      }}>
        {!collapsed && (
          <span style={{
            fontSize: "0.7rem", fontWeight: 700,
            letterSpacing: "0.08em", textTransform: "uppercase",
            color: "var(--muted)",
          }}>Website</span>
        )}
        <button
          onClick={onToggleCollapsed}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="cms-collapse-btn"
          style={{
            background: "transparent", border: "none",
            color: "var(--muted)", cursor: "pointer",
            padding: "2px 4px", borderRadius: 4,
            fontSize: "0.8rem", lineHeight: 1,
            opacity: 0.5, flexShrink: 0,
          }}
        >
          {collapsed ? "›" : "‹"}
        </button>
      </div>

      {/* Page list — scrollable */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        {loading ? (
          !collapsed && (
            <div style={{ padding: "0.5rem 1rem", color: "var(--muted)", fontSize: "0.85rem" }}>Loading…</div>
          )
        ) : (
          <nav aria-label="Pages">
            {homePage && (
              <HomePageRow
                page={homePage}
                isActive={homePage.ID === currentPageId}
                collapsed={collapsed}
                onClone={handleClone}
                onDelete={handleDelete}
              />
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={otherPages.map(p => p.ID)} strategy={verticalListSortingStrategy}>
                {otherPages.map(page => (
                  <SortablePageRow
                    key={page.ID}
                    page={page}
                    isActive={page.ID === currentPageId}
                    collapsed={collapsed}
                    onClone={handleClone}
                    onDelete={handleDelete}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </nav>
        )}

        {/* Inline create form — only in expanded mode */}
        {showCreate && !collapsed && (
          <form onSubmit={handleCreate} style={{ padding: "0.5rem 1rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
            <input
              autoFocus
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              placeholder="Page title"
              style={{
                fontSize: "0.875rem", padding: "0.35rem 0.5rem",
                borderRadius: 6, border: "1px solid var(--border)",
                background: "var(--bg)", color: "var(--text)", width: "100%",
              }}
            />
            {createError && <div style={{ fontSize: "0.75rem", color: "#ef4444" }}>{createError}</div>}
            <div style={{ display: "flex", gap: "0.35rem" }}>
              <button
                type="submit"
                disabled={creating || !newTitle.trim()}
                style={{
                  flex: 1, padding: "0.3rem 0.5rem", fontSize: "0.8rem",
                  background: "var(--accent)", color: "#fff",
                  border: "none", borderRadius: 6,
                  cursor: creating ? "not-allowed" : "pointer",
                }}
              >{creating ? "…" : "Create"}</button>
              <button
                type="button"
                onClick={() => { setShowCreate(false); setNewTitle(""); setCreateError(null); }}
                style={{
                  padding: "0.3rem 0.5rem", fontSize: "0.8rem",
                  background: "transparent", border: "1px solid var(--border)",
                  borderRadius: 6, color: "var(--muted)", cursor: "pointer",
                }}
              >Cancel</button>
            </div>
          </form>
        )}
      </div>

      {/* Footer — pinned to bottom */}
      <div style={{
        borderTop: "1px solid var(--border)",
        padding: collapsed ? "0.5rem 0" : "0.5rem 0.75rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
        flexShrink: 0,
      }}>
        {collapsed ? (
          // Collapsed: icon-only buttons centered
          <>
            <button
              onClick={handleNewPageClick}
              title="New page"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: 34, background: "transparent", border: "none",
                color: "var(--accent)", cursor: "pointer", fontSize: "1rem",
              }}
            >+</button>
            <a
              href="/app/portfolio"
              onClick={e => { e.preventDefault(); navigate("/app/portfolio"); }}
              title="Website Settings"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                height: 34, textDecoration: "none",
                color: isPortfolioActive ? "var(--accent)" : "var(--muted)",
                fontSize: "0.95rem",
              }}
            >&#9881;</a>
          </>
        ) : (
          // Expanded: full labels
          <>
            {!showCreate && (
              <button
                onClick={handleNewPageClick}
                style={{
                  display: "flex", alignItems: "center", gap: "0.4rem",
                  padding: "0.4rem 0.5rem", fontSize: "0.85rem",
                  background: "transparent", border: "none",
                  color: "var(--accent)", cursor: "pointer",
                  borderRadius: 6, textAlign: "left", width: "100%",
                }}
              >+ New page</button>
            )}
            <a
              href="/app/portfolio"
              onClick={e => { e.preventDefault(); navigate("/app/portfolio"); }}
              style={{
                display: "flex", alignItems: "center", gap: "0.4rem",
                padding: "0.4rem 0.5rem", fontSize: "0.85rem",
                textDecoration: "none",
                color: isPortfolioActive ? "var(--accent)" : "var(--text-secondary)",
                background: isPortfolioActive ? "color-mix(in srgb, var(--accent) 8%, transparent)" : "transparent",
                borderRadius: 6, transition: "background 0.1s",
              }}
            >
              <span style={{ fontSize: "0.9rem" }}>&#9881;</span>
              Website Settings
            </a>
          </>
        )}
      </div>

      {/* Hide collapse button on mobile (drawer handles open/close) */}
      <style>{`
        @media (max-width: 599px) { .cms-collapse-btn { display: none !important; } }
      `}</style>
    </aside>
  );
}
